import React from "react";
import { Button } from "app/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "app/components/ui/dialog";

export interface ActivationCongratulationsDialogViewProps {
  showDialog: boolean;
  setShowDialog: React.Dispatch<React.SetStateAction<boolean>>;
  description: React.ReactNode;
  extraDescriptions?: React.ReactNode;
  rows: string[];
  onContinue: () => void;
  continueLabel?: string;
  continueDisabled?: boolean;
}

/**
 * Shared shell for activation congratulations (activation-code / preloaded SIM).
 * Flow-specific copy, row data, and continue behavior live in separate modules.
 */
export const ActivationCongratulationsDialogView = ({
  showDialog,
  setShowDialog,
  description,
  extraDescriptions,
  rows,
  onContinue,
  continueLabel = "Continue",
  continueDisabled = false,
}: ActivationCongratulationsDialogViewProps) => {
  return (
    <Dialog open={showDialog} onOpenChange={setShowDialog}>
      <DialogContent className="top-1/2 gap-3.5 sm:gap-4 bg-white w-[95%] sm:max-w-[33.5rem] p-4 pb-5 sm:p-5 lg:p-6 lg:pt-[1.64rem] border-[#E6E7EB] rounded-lg sm:rounded-xl">
        <DialogHeader className="justify-center items-center p-0 bg-transparent">
          <div className="shrink-0 size-10 sm:size-11 md:size-12 lg:size-[3.125rem] mb-2 sm:mb-3 lg:mb-4">
            <img
              src="/assets/green-tick.svg"
              className="w-full h-auto"
              alt="Tick"
            />
          </div>
          <DialogTitle className="font-mobile text-black text-xl sm:text-[1.375rem] lg:text-2xl font-bold leading-6 sm:leading-[1.625rem] lg:leading-7">
            Congratulations!
          </DialogTitle>
          <DialogDescription className="text-[#3C3C3C] text-[0.825rem] sm:text-sm font-medium leading-4 sm:leading-5">
            {description}
          </DialogDescription>
          {extraDescriptions}
        </DialogHeader>
        <div className="">
          {rows.length > 0 ? (
            <div className="bg-[#F4F4F4] mb-5 sm:mb-6 lg:mb-[1.685rem]">
              {rows.map((row, index) => (
                <div
                  key={index}
                  className={`flex items-center ${
                    index === 0 ? "" : "border-t border-[#E5E7EB]"
                  }`}
                >
                  <span className="shrink-0 inline-flex justify-center items-center self-stretch w-8 sm:w-9 lg:w-[2.5625rem] p-2 sm:p-[0.55rem] lg:p-[0.625rem] border-r border-[#E5E7EB] text-[#1D1D1B] text-xs sm:text-[0.825rem] lg:text-sm font-semibold leading-5 sm:leading-[1.375rem] lg:leading-6">
                    {index + 1}
                  </span>
                  <p className="p-2 sm:p-[0.55rem] lg:p-[0.625rem] text-[#1D1D1B] text-xs sm:text-[0.825rem] lg:text-sm font-semibold leading-5 sm:leading-[1.375rem] lg:leading-6">
                    {row}
                  </p>
                </div>
              ))}
            </div>
          ) : null}
          <p className="text-[#3C3C3C] text-[0.8125rem] sm:text-[0.84375rem] lg:text-sm font-medium leading-4 sm:leading-5">
            Your privacy matters. Review our{" "}
            <a
              href="/privacy-policy"
              target="_blank"
              rel="noopener noreferrer"
              className="text-[#FE4D01] font-bold hover:underline"
            >
              Privacy Policy
            </a>
            .
          </p>
        </div>
        <Button
          type="button"
          variant="primary"
          className="h-[2.5rem] sm:h-[3rem] md:h-[3.25rem] lg:h-[3.375rem]"
          onClick={onContinue}
          disabled={continueDisabled}
        >
          {continueLabel}
        </Button>
      </DialogContent>
    </Dialog>
  );
};
